import Container from '@brands/Components/Container/Container';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import ChangePasswordModal from '@brands/Dashboard/Sidenav/SidenavModals/ChangePasswordModal/ChangePasswordModal';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { mapProfileToMyProfileSubmit } from '@brands/store/actions/careAdvisorAction';
import { selectAuth } from '@brands/store/selectors/auth';
import { setCareAdvisorMyProfile } from '@brands/store/slices/careAdvisorMyProfile';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MultiValue } from 'react-select';

import { Loading } from '../../../Components/LoadingSpinner/Loading';
import { useAppDispatch, useAppSelector } from '../../../hooks/useReduxHook';
import { defaultUserValues } from '../../../services/cases/types/defaultUserValues';
import { getCurrentProfile } from '../../../services/identity/getCurrentProfile';
import { getUserById } from '../../../services/identity/getUserById';
import { listAllClientTags } from '../../../services/identity/listAllClientTags';
import { scopeClientTagOptionsToActor } from '../../../services/identity/clientTagActorScope';
import { listAllLanguages } from '../../../services/identity/listAllLanguages';
import { getNetworkOptionsForRole, listNetworkOptions, NetworkOption } from '../../../services/identity/networkOptions';
import { ClientTag, Language, UserProfile, UserRoleName } from '../../../services/identity/types/UserProfile';
import { displayErrorDetails } from '../../../Utils/displayError';
import { OptionLanguage } from '../../../Utils/selectOptions';
import ChangeUserStatusModal from '../Modal/ChangeUserStatusModal';
import ResetPasswordModal from '../Modal/ResetPasswordModal';
import styles from '../styles.module.scss';
import EditProfile from './Sections/EditProfile';
import { CAMyProfileSubmit, ClientTagOption } from './utils/types';

export type { ClientTagOption };

type ReviewMyProfileProps = {
  reviewProfile?: boolean;
};

const MyProfile = ({ reviewProfile = false }: ReviewMyProfileProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const userId = useParams().userId as string;
  const { userInfo } = useAppSelector(selectAuth);
  const [thisUser, setThisUser] = useState<UserProfile>(userInfo || defaultUserValues);
  const [loading, setLoading] = useState(true);
  const [avatarAction, setAvatarAction] = useState<string>('');
  const [currentAvatarURL, setCurrentAvatarURL] = useState<string>('');
  const [image, setImage] = React.useState<string>();
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [resetPasswordConfirmation, setResetPasswordConfirmation] = useState<{ isOpen: boolean; userState: string }>({
    isOpen: false,
    userState: thisUser.state || '',
  });
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [changeUserStatusFlag, setChangeUserStatusFlag] = useState('');
  const [myProfileForm, setMyProfileForm] = useState<CAMyProfileSubmit>({} as CAMyProfileSubmit);
  const [clientTagOptions, setClientTagOptions] = useState<ClientTagOption[]>([]);
  const [clientTags, setClientTags] = useState<ClientTag[]>([]);
  const [networkOptions, setNetworkOptions] = useState<NetworkOption[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const { showModal, confirmNavigation, saveChanges, hideModal } = useUnsavedChangesWarning(isDirty, formRef);

  const disableButton = (): void => {
    setResetEmailSent(true);
  };

  const disableDeleteButton = (): void => {
    setThisUser({
      ...thisUser,
      status: 'Deactivated',
    });
  };

  const getCurrentUser = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      if (reviewProfile && userId) {
        const res = await getUserById(Number(id));
        setThisUser(res);
      }
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (): Promise<void> => {
    const userDetails = await getCurrentProfile('CareAdvisor');
    setThisUser(userDetails);
  };

  useEffect(() => {
    (async (): Promise<void> => {
      if (userId) {
        await getCurrentUser(userId);
      } else {
        await fetchUserDetails();
      }
      setLoading(false);
    })();
  }, [userId]);

  const fetchLanguageOptions = async (): Promise<void> => {
    const data = await listAllLanguages();
    const transformedData = data.map((language) => ({
      value: language.locale,
      label: language.name,
      id: language.id,
    }));
    setLanguageOptions(transformedData);
  };

  useEffect(() => {
    fetchLanguageOptions();
  }, []);

  useEffect(() => {
    if (!reviewProfile) return;
    listAllClientTags()
      .then(({ nodes }) => {
        const mapped = (nodes || []).map((c) => ({ id: String(c.id), value: c.id, label: c.name }));
        setClientTagOptions(scopeClientTagOptionsToActor(mapped, userInfo));
        setClientTags(nodes || []);
      })
      .catch(() => {
        setClientTagOptions([]);
        setClientTags([]);
      });
  }, [reviewProfile, userInfo]);

  useEffect(() => {
    if (!reviewProfile || (userInfo.role.name !== UserRoleName.Admin && userInfo.role.name !== UserRoleName.SuperAdmin)) {
      return;
    }
    listNetworkOptions(userInfo.role.name)
      .then((catalog) => setNetworkOptions(getNetworkOptionsForRole(catalog, UserRoleName.CareAdvisor)))
      .catch(() => setNetworkOptions([]));
  }, [reviewProfile, userInfo]);

  const fetchProfile = (): void => {
    const mappedProfile = mapProfileToMyProfileSubmit(thisUser);

    function isLanguageArray(value: Language[] | string[]): value is Language[] {
      return Array.isArray(value) && value.length > 0 && value[0] instanceof Object && 'id' in value[0];
    }

    let selectedLanguage: MultiValue<OptionLanguage> = [];
    if (isLanguageArray(mappedProfile.languages)) {
      for (let i = 0; i < languageOptions.length; i++) {
        for (let j = 0; j < mappedProfile.languages.length; j++) {
          if (Number(languageOptions[i].id) === Number(mappedProfile.languages[j].id)) {
            selectedLanguage = [...selectedLanguage, languageOptions[i]];
          }
        }
      }
    }

    dispatch(
      setCareAdvisorMyProfile({
        ...(mappedProfile as CAMyProfileSubmit),
        language: selectedLanguage,
      }),
    );
    setMyProfileForm({
      ...mappedProfile,
      language: selectedLanguage,
      profile_img: currentAvatarURL,
      avatar: thisUser.picture_media_id ? String(thisUser.picture_media_id) : '',
      client_tag_ids: (thisUser.clients ?? []).map((c) => String(c.id)).sort(),
      client_division_ids: (thisUser.divisions ?? []).map((d) => String(d.id)).sort(),
      network: thisUser.network ?? null,
    });
  };

  useEffect(() => {
    if (loading) return;
    fetchProfile();
  }, [thisUser, languageOptions, loading]);

  if (loading) {
    return <Loading fullScreen />;
  }

  const editProfileElement = (
    <>
      <EditProfile
        formRef={formRef}
        setIsDirty={setIsDirty}
        myProfileForm={myProfileForm}
        setMyProfileForm={setMyProfileForm}
        setImage={setImage}
        image={image}
        setCurrentAvatarURL={setCurrentAvatarURL}
        currentAvatarURL={currentAvatarURL}
        thisUser={thisUser}
        setThisUser={setThisUser}
        avatarAction={avatarAction}
        setAvatarAction={setAvatarAction}
        reviewProfile={reviewProfile}
        languageOptions={languageOptions}
        setLoading={setLoading}
        isSavingProfile={isSavingProfile}
        setIsSavingProfile={setIsSavingProfile}
        userId={userId}
        resetEmailSent={resetEmailSent}
        setResetPasswordConfirmation={setResetPasswordConfirmation}
        setChangeUserStatusFlag={setChangeUserStatusFlag}
        clientTagOptions={clientTagOptions}
        clientTagCatalog={clientTags}
        networkOptions={networkOptions}
      />
      {showModal && (
        <UnsavedChangesModal
          isOpen={showModal}
          onConfirm={confirmNavigation}
          saveChange={saveChanges}
          loading={isSavingProfile}
          hideModal={hideModal}
        />
      )}
      {resetPasswordConfirmation.isOpen && reviewProfile && (
        <ResetPasswordModal
          setOpenModal={setResetPasswordConfirmation}
          disableButton={disableButton}
          userId={Number(userId)}
          userEmail={thisUser.email}
          userName={thisUser.full_name}
          isDeactivated={thisUser.status === 'Deactivated'}
          userState={resetPasswordConfirmation.userState}
        />
      )}
      {resetPasswordConfirmation.isOpen && !reviewProfile && (
        <ChangePasswordModal
          isOpen={resetPasswordConfirmation.isOpen}
          onClose={() => setResetPasswordConfirmation({ isOpen: false, userState: '' })}
          email={thisUser.email || userInfo.email || ''}
        />
      )}
      {changeUserStatusFlag && (
        <ChangeUserStatusModal
          setOpenModal={setChangeUserStatusFlag}
          disableButton={disableDeleteButton}
          userId={Number(userId)}
          userName={thisUser.full_name}
          status={changeUserStatusFlag}
          thisUser={thisUser}
          setThisUser={setThisUser}
        />
      )}
    </>
  );

  if (reviewProfile) {
    return editProfileElement;
  }

  return (
    <Container patientDashboard childClassName={styles.dashboard} isSidenavVisible>
      {editProfileElement}
    </Container>
  );
};
export default MyProfile;
